import { useEffect, useRef } from "react";

// Abstract world map: landmass as row spans on a 100 x 50 grid (x 0-100, y 0-50)
const LAND = [
  [4, 31, 37],
  [5, 10, 26], [5, 30, 38], [5, 60, 78],
  [6, 8, 30], [6, 32, 38], [6, 58, 84],
  [7, 7, 33], [7, 33, 37], [7, 56, 90],
  [8, 6, 34], [8, 40, 44], [8, 55, 94],
  [9, 7, 34], [9, 38, 46], [9, 55, 96],
  [10, 8, 33], [10, 37, 47], [10, 54, 97],
  [11, 9, 32], [11, 40, 48], [11, 53, 98],
  [12, 10, 31], [12, 43, 49], [12, 53, 98],
  [13, 11, 30], [13, 44, 52], [13, 54, 97],
  [14, 12, 28], [14, 44, 56], [14, 55, 96],
  [15, 13, 27], [15, 43, 58], [15, 55, 95],
  [16, 14, 26], [16, 43, 60], [16, 56, 93],
  [17, 15, 25], [17, 44, 62], [17, 57, 92],
  [18, 16, 24], [18, 45, 63], [18, 58, 90],
  [19, 17, 23], [19, 46, 63], [19, 59, 88],
  [20, 18, 22], [20, 46, 64], [20, 60, 87],
  [21, 19, 21], [21, 47, 64], [21, 61, 85],
  [22, 19, 21], [22, 47, 64], [22, 62, 84],
  [23, 20, 22], [23, 48, 64], [23, 63, 83],
  [24, 24, 27], [24, 48, 63], [24, 64, 82],
  [25, 24, 29], [25, 47, 62], [25, 66, 81],
  [26, 24, 30], [26, 46, 61], [26, 67, 80],
  [27, 25, 31], [27, 45, 61], [27, 69, 79],
  [28, 25, 32], [28, 45, 60], [28, 71, 78],
  [29, 25, 33], [29, 44, 60], [29, 72, 78],
  [30, 25, 34], [30, 44, 60], [30, 73, 78],
  [31, 25, 34], [31, 44, 60], [31, 74, 78],
  [32, 25, 34], [32, 45, 60], [32, 73, 79],
  [33, 25, 33], [33, 45, 59], [33, 61, 62], [33, 72, 80], [33, 74, 80],
  [34, 26, 33], [34, 46, 59], [34, 61, 62], [34, 71, 82], [34, 76, 85],
  [35, 26, 32], [35, 47, 58], [35, 61, 62], [35, 79, 87],
  [36, 26, 32], [36, 48, 58], [36, 83, 88],
  [37, 27, 31], [37, 49, 57],
  [38, 27, 30], [38, 50, 56],
  [39, 27, 30], [39, 50, 55], [39, 80, 89],
  [40, 27, 29], [40, 51, 54], [40, 79, 91],
  [41, 28, 29], [41, 52, 54], [41, 79, 92],
  [42, 79, 92],
  [43, 80, 91],
  [44, 81, 90],
];

// Market nodes: [x, y, colour]
const NODES = [
  [21, 17, "blue"],   // Houston
  [27, 14, "gold"],   // New York
  [28, 33, "blue"],   // Sao Paulo
  [45, 13, "gold"],   // London
  [50, 11, "blue"],   // Stavanger
  [51, 23, "blue"],   // Lagos
  [62, 25, "gold"],   // Dubai
  [74, 36, "blue"],   // Singapore
  [84, 42, "gold"],   // Perth
  [88, 17, "blue"],   // Tokyo
];

const LINKS = [
  [0, 3], [1, 3], [2, 5], [3, 4], [3, 6], [5, 6], [6, 7], [7, 8], [7, 9],
];

export default function WorldMap({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let dots = [];
    let unit = 0;
    let ox = 0;
    let oy = 0;
    let raf = null;
    const t0 = performance.now();

    const nodePos = (i) => ({
      x: ox + NODES[i][0] * unit,
      y: oy + NODES[i][1] * unit,
    });

    const build = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      unit = Math.min(w / 108, h / 56);
      ox = (w - 100 * unit) / 2;
      oy = (h - 50 * unit) / 2;
      const step = canvas.clientWidth < 640 ? 2.0 : 1.2;
      dots = [];
      for (const [y, x0, x1] of LAND) {
        for (let x = x0; x <= x1; x += step) {
          const hsh = Math.abs(Math.sin(x * 12.9898 + y * 78.233) * 43758.5453) % 1;
          const edge = x - x0 < step || x1 - x < step;
          if (hsh < 0.14 && !edge) continue;
          dots.push({
            x: ox + (x + (hsh - 0.5) * 0.3) * unit,
            y: oy + (y + (hsh - 0.5) * 0.3) * unit,
            r: unit * (edge ? 0.3 : 0.26),
            phase: hsh * Math.PI * 2,
            i: (edge ? 0.85 : 0.5) + hsh * 0.5,
          });
        }
      }
    };

    const draw = (now) => {
      const t = (now - t0) / 1000;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      for (const d of dots) {
        const tw = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(t * 0.55 + d.phase);
        ctx.fillStyle = `rgba(226,232,240,${(0.06 + 0.14 * tw * d.i).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, 6.2832);
        ctx.fill();
      }

      ctx.lineWidth = 1;
      ctx.lineCap = "round";
      ctx.setLineDash([4, 7]);
      ctx.lineDashOffset = reduced ? 0 : -t * 6;
      for (const [a, b] of LINKS) {
        const A = nodePos(a);
        const B = nodePos(b);
        const mx = (A.x + B.x) / 2;
        const my = Math.min(A.y, B.y) - Math.hypot(B.x - A.x, B.y - A.y) * 0.18;
        const g = ctx.createLinearGradient(A.x, A.y, B.x, B.y);
        g.addColorStop(0, "rgba(56,189,248,0.06)");
        g.addColorStop(0.5, "rgba(56,189,248,0.30)");
        g.addColorStop(1, "rgba(212,175,55,0.06)");
        ctx.strokeStyle = g;
        ctx.beginPath();
        ctx.moveTo(A.x, A.y);
        ctx.quadraticCurveTo(mx, my, B.x, B.y);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      NODES.forEach((n, i) => {
        const p = nodePos(i);
        const pulse = reduced ? 0.5 : 0.5 + 0.5 * Math.sin(t * 1.1 + i * 1.7);
        const col = n[2] === "gold" ? "212,175,55" : "56,189,248";
        const glowR = unit * (2.2 + 1.2 * pulse);
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR * 1.7);
        g.addColorStop(0, `rgba(${col},${(0.42 + 0.25 * pulse).toFixed(3)})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowR * 1.7, 0, 6.2832);
        ctx.fill();
        ctx.strokeStyle = `rgba(${col},${(0.25 + 0.2 * pulse).toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4.5 + pulse * 1.5, 0, 6.2832);
        ctx.stroke();
        ctx.fillStyle = `rgba(${col},1)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.9 + pulse * 1.0, 0, 6.2832);
        ctx.fill();
      });

      if (!reduced) raf = requestAnimationFrame(draw);
    };

    build();
    raf = requestAnimationFrame(draw);
    const onResize = () => {
      build();
      if (reduced) draw(performance.now());
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className={className}
      role="img"
      aria-label="Abstract world map of illuminated dots showing global markets connected by fine lines, with gold and blue points marking commercial hubs"
    />
  );
}
